import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { todosLosProductos } from '../data/productos';
import { routes, generateProductSlug, extractProductIdFromSlug, getUrlParams } from '../config/routes';
import './Productos.css';

const Productos = () => {
  const navigate = useNavigate();
  
  // Usar getUrlParams para manejar el mapeo de URLs
  const { categoryId, productSlug } = getUrlParams(window.location.pathname);
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedOption2, setSelectedOption2] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryPages, setCategoryPages] = useState({}); // Almacenar página de cada categoría
  const itemsPerPage = 6;

  // Función para filtrar capacidades según dimensión seleccionada (específica para balanza pedestal)
  const filtrarCapacidadesPorDimension = (producto, dimensionSeleccionada) => {
    // Solo aplicar filtro para balanza pedestal (ID: 205)
    if (producto.id !== 205) {
      return producto.opciones2 || [];
    }

    // Reglas de compatibilidad para balanza pedestal
    const reglasCompatibilidad = {
      'dimension-30x40': ['30kg', '60kg', '100kg', '200kg'],
      'dimension-45x60': ['30kg', '60kg', '100kg', '200kg', '300kg', '500kg'],
      'dimension-personalizada': ['30kg', '60kg', '100kg', '200kg', '300kg', '500kg']
    };

    const capacidadesPermitidas = reglasCompatibilidad[dimensionSeleccionada];
    
    if (!capacidadesPermitidas) {
      return producto.opciones2 || [];
    }

    return (producto.opciones2 || []).filter(capacidad => 
      capacidadesPermitidas.includes(capacidad.nombre)
    );
  };

  // Usar productos importados
  const productos = todosLosProductos;

  const categorias = useMemo(() => [
    { 
      id: 'balanzas', 
      name: 'Balanzas', 
      imagen: '/imagenes/categorias_productos/balanzas.webp',
      descripcion: 'Balanzas de precisión para laboratorio e industria'
    },
    { 
      id: 'plataformas', 
      name: 'Balanzas de piso', 
      imagen: 'imagenes/categorias_productos/plataforma.webp',
      descripcion: 'Plataformas de pesaje industriales'
    },
    { 
      id: 'basculas-camiones', 
      name: 'Básculas de Camiones', 
      imagen: 'imagenes/categorias_productos/bascula_camiones.webp',
      descripcion: 'Básculas especializadas para camiones'
    },
    { 
      id: 'pesaje-dinamico', 
      name: 'Sistemas y Soluciones de Pesaje Industrial', 
      imagen: 'imagenes/categorias_productos/pesaje_dinamico.webp',
      descripcion: 'Sistemas de pesaje en movimiento'
    },
    { 
      id: 'etiquetado', 
      name: 'Accesorios y Repuestos', 
      imagen: '/imagenes/categorias_productos/etiquetado.webp',
      descripcion: 'Accesorios y repuestos para equipos de pesaje'
    },
    { 
      id: 'indicadores',
      name: 'Indicadores',
      imagen: '/imagenes/productos/Indicadores/lp1.webp',
      descripcion: 'Indicadores digitales para sistemas de pesaje'
    },
    { 
      id: 'basculas-colgar', 
      name: 'Básculas de Colgar', 
      imagen: '/imagenes/categorias_productos/basculas_colgar.webp',
      descripcion: 'Básculas de colgar para aplicaciones industriales'
    },
    { 
      id: 'masas-patrones', 
      name: 'Masas Patrones', 
      imagen: '/imagenes/categorias_productos/masas_patrones.webp',
      descripcion: 'Masas patrones certificadas para calibración de equipos de pesaje'
    },
    { 
      id: 'celdas-cargas',
      name: 'Celdas de Cargas',
      imagen: '/imagenes/productos/CeldasCargas/ccamiones1.webp',
      descripcion: 'Celdas de carga de alta precisión para sistemas de pesaje industrial'
    },
    { 
      id: 'balanzas-pedestal-packing', 
      name: 'Balanzas de pedestal (packing)', 
      imagen: '/imagenes/categorias_productos/balanzas_pedestal_packing.webp',
      descripcion: 'Balanzas de pedestal especializadas para operaciones de packing y empaque'
    }
  ], []);

  const productosFiltrados = selectedCategory === 'todos' 
    ? productos 
    : productos.filter(producto => producto.categoria === selectedCategory);

  // Lógica de paginación
  const totalPages = Math.ceil(productosFiltrados.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const productosPaginados = productosFiltrados.slice(startIndex, endIndex);



  // Efecto para manejar la navegación por URL
  useEffect(() => {
    if (categoryId) {
      // Validar que la categoría existe
      const categoriaExiste = categorias.find(cat => cat.id === categoryId);
      if (!categoriaExiste) {
        navigate('/404');
        return;
      }
      
      setSelectedCategory(categoryId);
      
      // Si hay categoryId pero NO hay productSlug, limpiar completamente el producto
      if (!productSlug) {
        setSelectedProduct(null);
        setSelectedOption('');
        setSelectedOption2('');
        setCurrentImageIndex(0);
      }
    } else {
      // Si no hay categoryId, resetear a estado inicial
      setSelectedCategory('todos');
      setSelectedProduct(null);
      setSelectedOption('');
      setSelectedOption2('');
      setCurrentImageIndex(0);
    }
    
    if (productSlug) {
      const productIdFromSlug = extractProductIdFromSlug(productSlug);
      const product = productos.find(p => p.id === productIdFromSlug);
      if (!product) {
        navigate('/404');
        return;
      }
      // Solo actualizar si es un producto diferente
      if (!selectedProduct || selectedProduct.id !== product.id) {
        setSelectedProduct(product);
        setSelectedOption('');
        setSelectedOption2('');
        setCurrentImageIndex(0);
      }
    }

    // Manejar paginación desde URL
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    if (pageParam) {
      const pageNumber = parseInt(pageParam, 10);
      if (pageNumber > 0) {
        setCurrentPage(pageNumber);
        // Guardar la página en el estado de la categoría
        if (categoryId) {
          setCategoryPages(prev => ({
            ...prev,
            [categoryId]: pageNumber
          }));
        }
      }
    } else {
      // Si no hay parámetro de página en la URL, usar la página guardada o 1
      if (categoryId) {
        const savedPage = categoryPages[categoryId] || 1;
        setCurrentPage(savedPage);
      } else {
        setCurrentPage(1);
      }
    }
  }, [categoryId, productSlug, productos, categorias, navigate, selectedProduct, categoryPages]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedProduct(null); // Resetear producto al cambiar categoría
    setSelectedOption(''); // Resetear opción seleccionada al cambiar categoría
    setCurrentImageIndex(0); // Resetear índice de imagen
    
    // Restaurar la página guardada de la categoría o usar página 1
    const savedPage = categoryPages[categoryId] || 1;
    setCurrentPage(savedPage);
    
    // Navegar a la categoría con la página correcta
    const baseUrl = routes.productosCategoria(categoryId);
    const pageUrl = savedPage > 1 ? `${baseUrl}?page=${savedPage}` : baseUrl;
    navigate(pageUrl);
    
    // Scroll al inicio de la página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
    setSelectedOption(''); // Resetear opción al seleccionar producto
    navigate(routes.productosDetalle(product.categoria, generateProductSlug(product)));
    // Scroll al inicio de la página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Efecto para validar currentImageIndex
  useEffect(() => {
    if (selectedProduct && selectedProduct.imagenes) {
      if (selectedProduct.imagenes.length === 0) {
        setCurrentImageIndex(0);
      } else if (currentImageIndex >= selectedProduct.imagenes.length) {
        setCurrentImageIndex(0);
      }
    }
  }, [selectedProduct, currentImageIndex]);

  const handleVolver = () => {
    if (selectedProduct) {
      // Volver a la categoría del producto manteniendo la página actual
      const baseUrl = routes.productosCategoria(selectedProduct.categoria);
      const pageUrl = currentPage > 1 ? `${baseUrl}?page=${currentPage}` : baseUrl;
      navigate(pageUrl);
      setSelectedProduct(null);
      setSelectedOption('');
      setSelectedOption2('');
      setCurrentImageIndex(0);
    } else if (selectedCategory !== 'todos') {
      // Si hay una categoría seleccionada, volver a la página principal de productos
      navigate(routes.productos);
      setSelectedCategory('todos');
      setSelectedProduct(null);
      setSelectedOption('');
      setSelectedOption2('');
      setCurrentImageIndex(0);
      setCurrentPage(1);
    } else {
      // Volver a la página principal de productos
      navigate(routes.productos);
      setSelectedCategory('todos');
      setSelectedProduct(null);
      setSelectedOption('');
      setSelectedOption2('');
      setCurrentImageIndex(0);
      setCurrentPage(1);
    }
  };

  // Funciones para breadcrumbs
  const handleBreadcrumbInicio = () => {
    navigate(routes.home);
    setSelectedCategory('todos');
    setSelectedProduct(null);
    setSelectedOption('');
    setCurrentImageIndex(0);
  };

  const handleBreadcrumbProductos = () => {
    navigate(routes.productos);
    setSelectedCategory('todos');
    setSelectedProduct(null);
    setSelectedOption('');
    setCurrentImageIndex(0);
  };

  const handleBreadcrumbCategoria = () => {
    if (selectedProduct) {
      // Si hay un producto seleccionado, ir a su categoría manteniendo la página actual
      const baseUrl = routes.productosCategoria(selectedProduct.categoria);
      const pageUrl = currentPage > 1 ? `${baseUrl}?page=${currentPage}` : baseUrl;
      navigate(pageUrl);
      setSelectedProduct(null);
      setSelectedOption('');
      setCurrentImageIndex(0);
    } else if (selectedCategory !== 'todos') {
      // Si hay una categoría seleccionada, ir a esa categoría manteniendo la página actual
      const baseUrl = routes.productosCategoria(selectedCategory);
      const pageUrl = currentPage > 1 ? `${baseUrl}?page=${currentPage}` : baseUrl;
      navigate(pageUrl);
    }
  };

  // Resetear estado automáticamente cuando la ruta es /productos
  useEffect(() => {
    if (!categoryId && !productSlug) {
      setSelectedCategory('todos');
      setSelectedProduct(null);
      setSelectedOption('');
      setSelectedOption2('');
      setCurrentImageIndex(0);
    }
  }, [categoryId, productSlug]);

  const handleOptionSelect = (optionId) => {
    setSelectedOption(optionId);
    setSelectedOption2(''); // Resetear segunda opción cuando cambia la primera
  };

  const handleOptionSelect2 = (optionId) => {
    setSelectedOption2(optionId);
  };

  const handleCotizar = () => {
    if (selectedProduct && selectedOption) {
      const option = selectedProduct.opciones.find(opt => opt.id === selectedOption);
      const option2 = selectedProduct.opciones2 && selectedOption2 ? 
        selectedProduct.opciones2.find(opt => opt.id === selectedOption2) : null;
      
      let message = `Hola, me interesa cotizar el producto: ${selectedProduct.nombre} - ${option.nombre}`;
      if (option2) {
        message += ` - ${option2.nombre}`;
      }
      
              window.open(`https://wa.me/+56976234326?text=${encodeURIComponent(message)}`, '_blank');
    }
  };

  const nextImage = () => {
    if (selectedProduct && selectedProduct.imagenes && selectedProduct.imagenes.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === selectedProduct.imagenes.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProduct && selectedProduct.imagenes && selectedProduct.imagenes.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? selectedProduct.imagenes.length - 1 : prevIndex - 1
      );
    }
  };

  const selectImage = (index) => {
    if (selectedProduct && selectedProduct.imagenes && selectedProduct.imagenes.length > 0 && index >= 0 && index < selectedProduct.imagenes.length) {
      setCurrentImageIndex(index);
    }
  };

  // Función para abrir modal de zoom
  const openImageModal = () => {
    setIsImageModalOpen(true);
  };

  // Función para cerrar modal de zoom
  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  // Funciones de paginación
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedProduct(null);
    setSelectedOption('');
    setCurrentImageIndex(0);
    
    // Guardar la página actual en el estado de la categoría
    if (selectedCategory !== 'todos') {
      setCategoryPages(prev => ({
        ...prev,
        [selectedCategory]: pageNumber
      }));
    }
    
    // Actualizar URL con la página
    if (selectedCategory !== 'todos') {
      const baseUrl = routes.productosCategoria(selectedCategory);
      const pageUrl = pageNumber > 1 ? `${baseUrl}?page=${pageNumber}` : baseUrl;
      navigate(pageUrl);
    }
    
    // Scroll al inicio de la página
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  return (
    <>
      <div className="productos">
        {/* Breadcrumbs */}
        <div className="breadcrumbs">
          <div className="container">
            <span 
              className="breadcrumb-link" 
              onClick={handleBreadcrumbInicio}
              title="Ir al inicio"
            >
              Inicio
            </span> 
            {' > '} 
            <span 
              className="breadcrumb-link" 
              onClick={handleBreadcrumbProductos}
              title="Ver todos los productos"
            >
              Productos
            </span>
            {selectedCategory !== 'todos' && (
              <>
                {' > '} 
                <span 
                  className="breadcrumb-link" 
                  onClick={handleBreadcrumbCategoria}
                  title={`Ver productos de ${categorias.find(cat => cat.id === selectedCategory)?.name}`}
                >
                  {categorias.find(cat => cat.id === selectedCategory)?.name}
                </span>
              </>
            )}
            {selectedProduct && (
              <>
                {' > '} 
                <span className="breadcrumb-current" title={`Producto: ${selectedProduct.nombre}`}>
                  {selectedProduct.nombre}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="productos-layout">
          {/* Sidebar */}
          <aside className="productos-sidebar">
            <nav className="sidebar-nav">
              <p className="sidebar-title">Categorías</p>
              <ul className="categorias-list">
                <li className="categoria-item">
                  <button
                    className={`categoria-btn ${selectedCategory === 'todos' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedCategory('todos');
                      navigate('/productos');
                      // Scroll al inicio de la página
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Ver Todas las Categorías
                  </button>
                </li>
            {categorias.map((categoria) => (
                  <li key={categoria.id} className="categoria-item">
              <button
                      className={`categoria-btn ${selectedCategory === categoria.id ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(categoria.id)}
              >
                {categoria.name}
              </button>
                  </li>
                      ))}
                    </ul>
            </nav>
          </aside>

          {/* Main Content Area */}
          <main className="productos-main">
            {selectedCategory === 'todos' ? (
              // Mostrar categorías como tarjetas
              <div className="catalogo">
                <header className="catalogo-header">
                  <p className="eyebrow">Catálogo</p>
                  <h1 className="catalogo-titulo">Nuestros Productos</h1>
                  <p className="catalogo-subtitulo">
                    Explore nuestras {categorias.length} categorías de equipos de pesaje industrial
                  </p>
                </header>
                <div className="categorias-grid">
                  {categorias.map((categoria, index) => {
                    const cantidad = productos.filter(p => p.categoria === categoria.id).length;
                    return (
                      <div
                        key={categoria.id}
                        className="categoria-card"
                        style={{ '--stagger': `${index * 0.06}s` }}
                        onClick={() => handleCategoryClick(categoria.id)}
                      >
                        <div className="categoria-imagen">
                          <img src={categoria.imagen} alt={categoria.name} loading="lazy" />
                          {cantidad > 0 && (
                            <span className="categoria-cantidad">{cantidad} {cantidad === 1 ? 'producto' : 'productos'}</span>
                          )}
                        </div>
                        <div className="categoria-contenido">
                          <h3 className="categoria-titulo">{categoria.name}</h3>
                          <p className="categoria-descripcion">{categoria.descripcion}</p>
                          <span className="categoria-link">
                            Ver productos
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <line x1="4" y1="12" x2="20" y2="12" />
                              <polyline points="13 5 20 12 13 19" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : selectedProduct ? (
              // Mostrar detalle del producto
              <div className="producto-detalle">
                <div className="producto-detalle-layout">
                  <div className="producto-imagen-principal">
                    <div className="imagen-container">
                      <img
                        src={selectedProduct && selectedProduct.imagenes && selectedProduct.imagenes[currentImageIndex] ? selectedProduct.imagenes[currentImageIndex] : (selectedProduct ? selectedProduct.imagen : '')}
                        alt={selectedProduct ? selectedProduct.nombre : ''}
                        onClick={openImageModal}
                        className="imagen-principal-clickeable"
                      />
                      {selectedProduct && selectedProduct.imagenes && selectedProduct.imagenes.length > 1 && (
                        <>
                          <button className="btn-nav btn-prev" onClick={prevImage} aria-label="Imagen anterior">
                            ‹
                          </button>
                          <button className="btn-nav btn-next" onClick={nextImage} aria-label="Imagen siguiente">
                            ›
                          </button>
                        </>
                      )}
                    </div>

                    {selectedProduct && selectedProduct.imagenes && selectedProduct.imagenes.length > 1 && (
                      <div className="producto-miniaturas">
                        {selectedProduct.imagenes.map((imagen, index) => (
                          <button
                            key={index}
                            className={`miniatura ${index === currentImageIndex ? 'activa' : ''}`}
                            onClick={() => selectImage(index)}
                            aria-label={`Ver imagen ${index + 1} de ${selectedProduct.imagenes.length}`}
                          >
                            <img src={imagen} alt="" loading="lazy" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="producto-info">
                    <div className="producto-header">
                      <div>
                        {selectedProduct && (
                          <p className="producto-eyebrow">
                            {categorias.find((cat) => cat.id === selectedProduct.categoria)?.name}
                          </p>
                        )}
                        <h1 className="producto-titulo-principal">{selectedProduct ? selectedProduct.nombre : ''}</h1>
                      </div>
                      <button
                        className="btn-volver"
                        onClick={handleVolver}
                      >
                        ← Volver
                      </button>
                    </div>
                    {selectedProduct && (selectedProduct.descripcion2 || selectedProduct.descripcion) && (
                      <p className="producto-resumen">
                        {selectedProduct.descripcion2 ? selectedProduct.descripcion2 : selectedProduct.descripcion}
                      </p>
                    )}
                    
                    <div className="producto-opciones">
                      <label className="opcion-label">Selecciona la versión</label>
                      <select 
                        className="opcion-select"
                        value={selectedOption}
                        onChange={(e) => handleOptionSelect(e.target.value)}
                      >
                        <option value="">Elige una versión</option>
                        {selectedProduct && selectedProduct.opciones && selectedProduct.opciones.map((opcion) => (
                          <option key={opcion.id} value={opcion.id}>
                            {opcion.nombre}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Segundo selector de opciones - aparece solo si se seleccionó la primera opción */}
                    {selectedOption && selectedProduct && selectedProduct.opciones2 && (
                      <div className="producto-opciones">
                        <label className="opcion-label">Selecciona la capacidad :</label>
                        <select 
                          className="opcion-select"
                          value={selectedOption2}
                          onChange={(e) => handleOptionSelect2(e.target.value)}
                        >
                          <option value="">Elige una capacidad</option>
                          {filtrarCapacidadesPorDimension(selectedProduct, selectedOption).map((opcion) => (
                            <option key={opcion.id} value={opcion.id}>
                              {opcion.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <button 
                      className="btn-cotizar"
                      onClick={handleCotizar}
                      disabled={!selectedOption || (selectedProduct && selectedProduct.opciones2 && !selectedOption2)}
                    >
                      Cotizar por WhatsApp
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="4" y1="12" x2="20" y2="12" />
                        <polyline points="13 5 20 12 13 19" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Tabla de Especificaciones Técnicas - Fuera del grid */}
                {selectedProduct && selectedProduct.tablaEspecificaciones && (
                  <div className="especificaciones-full">
                    <div className="producto-tabla-especificaciones">
                      <div className="tabla-header">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                          <line x1="9" y1="9" x2="9" y2="21" />
                        </svg>
                        <span>{selectedProduct.tablaEspecificaciones.titulo}</span>
                      </div>
                      <div className="tabla-contenido">
                        <table className="especificaciones-tabla">
                          <thead>
                            <tr>
                              {selectedProduct.tablaEspecificaciones.columnas.map((columna, index) => (
                                <th key={index}>{columna}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {selectedProduct.tablaEspecificaciones.filas.map((fila, index) => (
                              <tr key={index}>
                                <td className="categoria-especificacion">{fila.categoria}</td>
                                {fila.valores.map((valor, valorIndex) => (
                                  <td 
                                    key={valorIndex}
                                    colSpan={fila.spanAll ? selectedProduct.tablaEspecificaciones.columnas.length - 1 : 1}
                                    className={fila.spanAll ? 'valor-span-all' : ''}
                                  >
                                    {valor}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
                {selectedProduct && selectedProduct.caracteristicas && (
                  <div className="caracteristicas-full">
                    <div className="producto-caracteristicas">
                      <div className="caracteristicas-header">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
                        </svg>
                        <span>Características Técnicas</span>
                      </div>
                      <div className="caracteristicas-contenido">
                        {selectedProduct.caracteristicas.map((seccion, index) => (
                          <div key={index} className="caracteristica-seccion">
                            <h4 className="caracteristica-titulo">{seccion.titulo}</h4>
                            <ul className="caracteristica-lista">
                              {seccion.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="caracteristica-item">
                                  {item.replace(/^[•\-–]\s*/, '')}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
            ) : (
              // Mostrar productos filtrados con paginación
              <div className="productos-container">
                <header className="catalogo-header categoria-header">
                  <p className="eyebrow">Catálogo</p>
                  <h1 className="catalogo-titulo">{categorias.find(cat => cat.id === selectedCategory)?.name}</h1>
                  <p className="catalogo-subtitulo">{categorias.find(cat => cat.id === selectedCategory)?.descripcion}</p>
                </header>
                <div className="productos-grid" key={`${selectedCategory}-${currentPage}`}>
                  {productosPaginados.map((producto, index) => (
                    <div
                      key={producto.id}
                      className="producto-card"
                      style={{ '--stagger': `${index * 0.06}s` }}
                      onClick={() => handleProductClick(producto)}
                    >
                      <div className="producto-imagenes">
                        <img
                          src={producto.imagen}
                          alt={producto.nombre}
                          className="producto-imagen"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="producto-titulo">{producto.nombre}</h3>
                      <p className="producto-descripcion">
                        {producto.descripcion}
                      </p>
                      <div className="producto-cta">
                        <button className="btn btn-primary">
                          Ver Detalles
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                  <div className="paginacion">
                    <div className="paginacion-info">
                      Página {currentPage} de {totalPages} - {productosFiltrados.length} productos
                    </div>
                    <div className="paginacion-controles">
                      <button 
                        className="btn-paginacion btn-prev"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                      >
                        ← Anterior
                      </button>
                      
                      <div className="paginacion-numeros">
                        {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                          <button
                            key={pageNumber}
                            className={`btn-paginacion ${pageNumber === currentPage ? 'active' : ''}`}
                            onClick={() => handlePageChange(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        ))}
                      </div>
                      
                      <button 
                        className="btn-paginacion btn-next"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                      >
                        Siguiente →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Modal de Zoom para Imágenes */}
      {isImageModalOpen && selectedProduct && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeImageModal}>
              ×
            </button>
            <div className="modal-image-container">
              <img 
                src={selectedProduct.imagenes && selectedProduct.imagenes[currentImageIndex] ? selectedProduct.imagenes[currentImageIndex] : selectedProduct.imagen} 
                alt={selectedProduct.nombre} 
                className="modal-image"
              />
            </div>
            <div className="modal-navigation">
              <button className="modal-nav-btn modal-prev" onClick={prevImage}>
                ‹
              </button>
              <button className="modal-nav-btn modal-next" onClick={nextImage}>
                ›
              </button>
            </div>
            <div className="modal-thumbnails">
              {selectedProduct.imagenes && selectedProduct.imagenes.length > 0 ? (
                selectedProduct.imagenes.map((imagen, index) => (
                  <div 
                    key={index} 
                    className={`modal-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => selectImage(index)}
                  >
                    <img src={imagen} alt={`${selectedProduct.nombre} ${index + 1}`} />
                  </div>
                ))
              ) : (
                <div className="modal-thumbnail active">
                  <img src={selectedProduct.imagen} alt={selectedProduct.nombre} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Productos; 